import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function MyWork() {
  const { tenantId, can } = useAuth();
  const [work, setWork] = useState({ items: [], exceptions: [] });

  const load = () => api.get("/my-work").then(({ data }) => setWork(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line

  const complete = async (item) => {
    try {
      await api.put(`/tenants/${item.tenant_id}/action-items/${item.id}`, { status: "done" });
      toast.success("Commitment closed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="my-work-header"
        eyebrow="Cross-engagement"
        title="My Work"
        description="Assignments, commitments and validations awaiting you across every engagement you can access."
      />

      <section>
        <p className="label-caps mb-4">Assigned to me ({work.items.length})</p>
        <div className="panel divide-y divide-border" data-testid="my-work-list">
          {work.items.map((i) => (
            <div key={`${i.item_type}-${i.id}`} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm">{i.title}</p>
                <p className="label-caps mt-1">
                  {i.tenant_name} · {i.framework || "platform"} · {i.item_type}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {i.due_date && <span className="font-mono text-xs tabular-nums">{i.due_date}</span>}
                <StateBadge state={i.status} />
                {i.item_type === "action" && can("edit") && (
                  <Button
                    data-testid={`complete-action-${i.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                    onClick={() => complete(i)}
                  >
                    Mark done
                  </Button>
                )}
              </div>
            </div>
          ))}
          {work.items.length === 0 && (
            <EmptyState testid="my-work-empty" title="Queue is clear" message="No assignments or commitments are open for you." />
          )}
        </div>
      </section>

      <section>
        <p className="label-caps mb-4">Engagement exceptions ({work.exceptions.length})</p>
        <div className="panel divide-y divide-border" data-testid="my-work-exceptions">
          {work.exceptions.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-4 px-5 py-4">
              <p className="truncate text-sm">{e.title}</p>
              <span className="font-mono text-xs tabular-nums text-destructive">Overdue since {e.due_date}</span>
            </div>
          ))}
          {work.exceptions.length === 0 && (
            <p className="px-5 py-8 text-sm text-muted-foreground">No overdue items visible at your clearance.</p>
          )}
        </div>
      </section>
    </div>
  );
}
